import { pagePatternDictionary } from '../legacyPages/my-account/constants';
import { productIdPrefix, skuPrefix } from '../constants/patterns';

/**
 * Tries to get logging function otherwise notify about fail
 * @returns {function|null} - function
 */
export function getApcLogService() {
    if (!!window && !!window.apcLogService) {
        return window.apcLogService;
    }
    console.warn('Failed to get tagging service');
    return null;
}

export function safeWrap(prop) {
    return (...args) => {
        const apcLogService = getApcLogService();
        if (!apcLogService) return null;
        return apcLogService[prop](...args);
    };
}

export function getLocale() {
    if (window && window.location && window.location.href && window.location.href.match) {
        const localeMatches = window.location.href.match(/\/[a-z]{2}\/[a-z]{2}\//i);
        if (localeMatches && localeMatches.length) {
            try {
                return localeMatches[0].split('/').filter(item => !!item);
            } catch (e) {
                console.error(e);
            }
        }
    }
    return ['us', 'en'];
}

export function matches(element, selector) {
    return (
        element.matches ||
        element.matchesSelector ||
        element.msMatchesSelector ||
        element.mozMatchesSelector ||
        element.webkitMatchesSelector ||
        element.oMatchesSelector ||
        ((e, s) => Array.from(document.querySelectorAll(s)).some(n => n === e))
    ).call(element, selector);
}

export function getParent(element) {
    if (!element) return null;
    if (element.parentElement) return element.parentElement;
    if (element.parentNode) return element.parentNode;
    return null;
}

export function closest(element, selector) {
    if (!element || !selector) return null;
    if (typeof element.closest === 'function') return element.closest(selector);
    let current = element;
    while (current && !matches(current, selector)) {
        current = getParent(current);
    }
    return current;
}

export function matchThisOrChildren(element, selector) {
    if (!element || !selector) return false;
    return (
        matches(element, selector) ||
        selector
            .split(',')
            .map(subSelector => subSelector.trim())
            .some(subSelector => matches(element, `${subSelector} *`))
    );
}

export function getData(element, key, defaultValue = null) {
    if (!element || !key) return defaultValue;
    return (element.dataset || {})[key] || defaultValue;
}

export function resolveMyAccountPageName(url) {
    const foundItem = pagePatternDictionary.find(item => item.pattern.test(url));
    return (foundItem || {}).pageName || '';
}

export function getFileId(url = '') {
    try {
        const docRef = url.match(/(p_Doc_Ref=)([^&]+)/);
        // eslint-disable-next-line no-useless-escape
        const lastSegment = url.match(/([^/.]+)(?:\.?[^/.])*$/);
        if (docRef) {
            return docRef[2];
        }
        if (lastSegment) {
            return lastSegment[1];
        }
    } catch (err) {
        console.error(err);
    }
    return '';
}

export function equalsInvariant(it, that) {
    return `${it}`.toUpperCase() === `${that}`.toUpperCase();
}

/**
 * Replaces a specific pattern with new one for all keys of object
 * @param obj {{}} - target object
 * @param pattern {string | RegExp} - property name pattern
 * @param newString {string} - string to be pasted instead
 * @returns {{}} - result object with updated properties names
 */
function replacePropertiesPattern(obj, pattern, newString) {
    const result = {};

    Object.keys(obj || {}).forEach(key => {
        result[key.replace(pattern, newString)] = obj[key];
    });

    return result;
}

/**
 * Removes specific pattern from all keys of object, for example:
 * removePropertiesPattern({ super_property: '123' }, 'super_') => { property: '123' }
 * @param obj {{}} - target object
 * @param pattern {string | RegExp} - property name pattern
 * @returns {{}} - result object without specified pattern in it's properties
 */
export function removePropertiesPattern(obj, pattern) {
    return replacePropertiesPattern(obj, pattern, '');
}

export function normalizeSku(sku) {
    return `${sku}`
        .toUpperCase()
        .replace(skuPrefix, '')
        .replace(productIdPrefix, '');
}

export function toDictionaryItem(selector, callback) {
    return { selector, callback };
}

export function collectEvent(functionName, target, data = {}) {
    const { tC } = window;
    if (!tC) {
        console.error('tC is not defined');
        return;
    }
    const typeFunc = tC.event && tC.event[functionName];
    if (!typeFunc) {
        console.error(`${functionName} is not defined in tC`);
        return;
    }

    try {
        typeFunc(target, data);
    } catch (error) {
        console.error('There is an error in TagCommander script:\r\n', error);
    }
}

export function setVariables(data, isReloadNeeded = false) {
    const currencyMeta = document.querySelector('meta[name="tagging-currency"]');
    const countryMeta = document.querySelector('meta[name="tagging-country"]');
    const languageMeta = document.querySelector('meta[name="tagging-language"]');
    const userIdMeta = document.querySelector('meta[name="tagging-user-id"]');
    const userCategoryMeta = document.querySelector('meta[name="tagging-user-category"]');
    const cartIdMeta = document.querySelector('meta[name="tagging-cart-id"]');

    const currency = currencyMeta ? currencyMeta.content : '';
    const country = countryMeta ? countryMeta.content : '';
    const language = languageMeta ? languageMeta.content : '';
    const userId = userIdMeta ? userIdMeta.content : '';
    const userCategory = userCategoryMeta ? userCategoryMeta.content : '';
    const cartId = cartIdMeta && cartIdMeta.content ? cartIdMeta.content : '';

    window.tc_vars = {
        ...(window.tc_vars || {}),
        env_work: window.env_work,
        env_country: country,
        env_language: language,
        env_currency: currency,
        user_id: userId,
        cart_id: cartId,
        user_category: userCategory,
        ...data,
    };

    if (
        isReloadNeeded &&
        window.tC &&
        window.tC.container &&
        typeof window.tC.container.reload === 'function'
    ) {
        window.tC.container.reload({ events: { page: [{}, {}] } });
    }
}
export function format(value) {
    return value
        .trim()
        .split(' ')
        .join('-');
}

export function saveEventsForNextPage(eventType, param = {}) {
    sessionStorage.setItem(
        'event',
        JSON.stringify({
            eventType,
            ...param,
        }),
    );
}
export function sendEventsFromPreviousPage(page) {
    try {
        const { apcLogService } = window;
        const event = sessionStorage.getItem('event');
        if (event) {
            const eventData = JSON.parse(event);
            const { eventType, ...param } = eventData;
            apcLogService(`${page}.${eventType}.click`, {
                target: null,
                ...param,
            });
            sessionStorage.removeItem('event');
        }
    } catch (e) {
        console.error(e);
    }
}
